import { useState } from 'react';
import { MagnifyingGlass, Package, Factory, ClipboardText, Truck, ArrowRight, Warning } from '@phosphor-icons/react';
import api from '../api';

const SAMPLE_BATCHES = ['BATCH-FG-2025-042', 'BATCH-FG-2024-031', 'BATCH-RM-2025-INK-007', 'INK-BL-20250115'];

function TraceNode({ icon: Icon, color, title, subtitle, badge, badgeColor, children, isLast }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div style={{ display: 'flex', gap: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: 16 }}>
        <div style={{ width: 36, height: 36, background: color, display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={16} color="#fff" weight="bold" />
        </div>
        {!isLast && <div style={{ width: 2, flex: 1, minHeight: 24, background: 'hsl(0,0%,18%)', margin: '4px 0' }} />}
      </div>
      <div style={{ flex: 1, marginBottom: isLast ? 0 : 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}
          onClick={() => setExpanded(e => !e)}>
          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 13, fontWeight: 700,
            color: 'hsl(0,0%,95%)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</div>
          {badge && (
            <span style={{ background: badgeColor || 'hsl(0,0%,14%)', color: '#fff', fontSize: 9,
              fontFamily: "'JetBrains Mono', monospace", padding: '2px 8px', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.08em' }}>{badge}</span>
          )}
          {subtitle && (
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              color: 'hsl(0,0%,45%)', textTransform: 'uppercase' }}>{subtitle}</span>
          )}
        </div>
        {expanded && children && (
          <div style={{ background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,14%)',
            padding: 16, marginTop: 4 }}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ label, value, accent }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
        color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{label}: </span>
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
        color: accent || 'hsl(0,0%,85%)', fontWeight: accent ? 600 : 400 }}>{value || '—'}</span>
    </div>
  );
}

function TraceResult({ result }) {
  if (!result.found) {
    return (
      <div style={{ padding: 32, textAlign: 'center', background: 'hsl(0,0%,5%)',
        border: '1px solid hsl(0,0%,14%)', marginTop: 16 }}>
        <Warning size={28} color="hsl(43,96%,56%)" style={{ marginBottom: 12 }} />
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12,
          color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          No records found for batch: <span style={{ color: 'hsl(43,96%,56%)' }}>{result.batch_no}</span>
        </div>
      </div>
    );
  }

  const { qc, work_order, sales_order, dispatch, stock_item, grn } = result;
  const isFG = result.type === 'FG';
  const nodes = [];

  if (isFG) {
    // Sales Order node
    if (sales_order) {
      nodes.push(
        <TraceNode key="so" icon={ClipboardText} color="hsl(197,100%,48%)"
          title="Sales Order" subtitle={sales_order.order_no}
          badge={sales_order.status?.replace(/_/g, ' ').toUpperCase()}
          badgeColor={sales_order.status === 'dispatched' ? 'hsl(140,60%,45%)' : 'hsl(197,100%,48%)'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <KV label="Order No" value={sales_order.order_no} accent="hsl(197,100%,48%)" />
              <KV label="Customer" value={sales_order.customer_name} />
              <KV label="Product" value={sales_order.product_name} />
            </div>
            <div>
              <KV label="Quantity" value={(sales_order.quantity || 0).toLocaleString()} />
              <KV label="Value" value={`₹${((sales_order.total_value || 0) / 1000).toFixed(0)}K`} accent="hsl(43,96%,56%)" />
              <KV label="Promise Date" value={sales_order.promise_date?.split('T')[0]} />
            </div>
          </div>
        </TraceNode>
      );
    }

    // Work Order node
    if (work_order) {
      nodes.push(
        <TraceNode key="wo" icon={Factory} color="hsl(12,90%,55%)"
          title="Work Order" subtitle={work_order.wo_no}
          badge={work_order.status?.replace(/_/g, ' ').toUpperCase()}
          badgeColor={work_order.status === 'completed' ? 'hsl(140,60%,45%)' : 'hsl(12,90%,55%)'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <KV label="WO No" value={work_order.wo_no} accent="hsl(12,90%,55%)" />
              <KV label="Line" value={work_order.line} />
              <KV label="Operator" value={work_order.operator} />
            </div>
            <div>
              <KV label="Planned Qty" value={(work_order.planned_qty || 0).toLocaleString()} />
              <KV label="Completed Qty" value={(work_order.completed_qty || 0).toLocaleString()} accent="hsl(140,60%,45%)" />
              <KV label="Yield" value={`${work_order.yield_pct || 0}%`}
                accent={work_order.yield_pct >= 95 ? 'hsl(140,60%,45%)' : 'hsl(43,96%,56%)'} />
            </div>
          </div>
        </TraceNode>
      );
    }

    // QC node
    if (qc) {
      const passed = qc.overall_status === 'pass';
      nodes.push(
        <TraceNode key="qc" icon={ClipboardText} color={passed ? 'hsl(140,60%,45%)' : 'hsl(0,84%,60%)'}
          title="QC Record" subtitle={qc.batch_no}
          badge={qc.overall_status?.toUpperCase()}
          badgeColor={passed ? 'hsl(140,60%,45%)' : 'hsl(0,84%,60%)'}>
          <div style={{ marginBottom: 8 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <div>
                <KV label="Batch No" value={qc.batch_no} accent="hsl(197,100%,48%)" />
                <KV label="QC Type" value={qc.qc_type?.toUpperCase()} />
                <KV label="Inspector" value={qc.inspector} />
              </div>
              <div>
                <KV label="Overall Status" value={qc.overall_status?.toUpperCase()}
                  accent={passed ? 'hsl(140,60%,45%)' : 'hsl(0,84%,60%)'} />
                <KV label="Notes" value={qc.notes} />
              </div>
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
              color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.1em',
              marginBottom: 6 }}>Parameters</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(qc.parameters || []).map((p, i) => (
                <div key={i} style={{ background: p.status === 'pass' ? 'hsl(140,60%,10%)' : 'hsl(0,84%,10%)',
                  border: `1px solid ${p.status === 'pass' ? 'hsl(140,60%,30%)' : 'hsl(0,84%,35%)'}`,
                  padding: '4px 8px', fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>
                  <span style={{ color: 'hsl(0,0%,70%)' }}>{p.name}</span>
                  <span style={{ color: 'hsl(0,0%,45%)', margin: '0 4px' }}>→</span>
                  <span style={{ color: p.status === 'pass' ? 'hsl(140,60%,55%)' : 'hsl(0,84%,65%)',
                    fontWeight: 600 }}>{p.actual}</span>
                </div>
              ))}
            </div>
          </div>
        </TraceNode>
      );
    }

    // Stock node
    if (stock_item) {
      nodes.push(
        <TraceNode key="stock" icon={Package} color="hsl(200,80%,50%)"
          title="FG Stock" subtitle={stock_item.item_code}
          badge={stock_item.qty_qc_hold > 0 ? 'QC HOLD' : 'AVAILABLE'}
          badgeColor={stock_item.qty_qc_hold > 0 ? 'hsl(0,84%,60%)' : 'hsl(140,60%,45%)'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <KV label="Item" value={stock_item.item_name} accent="hsl(200,80%,55%)" />
              <KV label="Location" value={stock_item.location} />
            </div>
            <div>
              <KV label="Available" value={`${(stock_item.qty_available || 0).toLocaleString()} ${stock_item.unit}`} />
              <KV label="QC Hold" value={`${(stock_item.qty_qc_hold || 0).toLocaleString()} ${stock_item.unit}`}
                accent={stock_item.qty_qc_hold > 0 ? 'hsl(0,84%,60%)' : undefined} />
            </div>
          </div>
        </TraceNode>
      );
    }

    // Dispatch node
    if (dispatch) {
      nodes.push(
        <TraceNode key="dispatch" icon={Truck} color="hsl(43,96%,56%)" isLast
          title="Dispatch Note" subtitle={dispatch.dispatch_no}
          badge={dispatch.status?.toUpperCase()}
          badgeColor={dispatch.status === 'dispatched' ? 'hsl(140,60%,45%)' : 'hsl(43,96%,40%)'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <KV label="Dispatch No" value={dispatch.dispatch_no} accent="hsl(43,96%,56%)" />
              <KV label="Customer" value={dispatch.customer_name} />
              <KV label="Qty" value={(dispatch.quantity || 0).toLocaleString()} />
            </div>
            <div>
              <KV label="Transporter" value={dispatch.transporter} />
              <KV label="Vehicle No" value={dispatch.vehicle_no} />
              <KV label="Invoice No" value={dispatch.invoice_no} />
            </div>
          </div>
        </TraceNode>
      );
    } else {
      // No dispatch yet — show final marker
      if (nodes.length > 0) {
        nodes.push(
          <div key="no-dispatch" style={{ display: 'flex', gap: 16, alignItems: 'center',
            opacity: 0.5, marginTop: 4 }}>
            <div style={{ width: 36, height: 36, border: '2px dashed hsl(0,0%,25%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Truck size={14} color="hsl(0,0%,40%)" />
            </div>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
              color: 'hsl(0,0%,35%)', textTransform: 'uppercase' }}>Dispatch not yet created</span>
          </div>
        );
      }
    }
  } else {
    // RM batch
    if (grn) {
      nodes.push(
        <TraceNode key="grn" icon={Package} color="hsl(197,100%,48%)"
          title="GRN" subtitle={grn.grn_no}
          badge={grn.status?.toUpperCase()} badgeColor="hsl(140,60%,45%)">
          <KV label="GRN No" value={grn.grn_no} accent="hsl(197,100%,48%)" />
          <KV label="PO No" value={grn.po_no} />
          <KV label="Vendor" value={grn.vendor_name} />
          <KV label="Notes" value={grn.notes} />
        </TraceNode>
      );
    }
    if (stock_item) {
      nodes.push(
        <TraceNode key="rm-stock" icon={Package} color="hsl(200,80%,50%)" isLast
          title="RM Stock" subtitle={stock_item.item_code}
          badge={(stock_item.qty_available || 0) < (stock_item.reorder_level || 0) ? 'BELOW REORDER' : 'IN STOCK'}
          badgeColor={(stock_item.qty_available || 0) < (stock_item.reorder_level || 0) ? 'hsl(0,84%,60%)' : 'hsl(140,60%,45%)'}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <KV label="Item" value={stock_item.item_name} accent="hsl(200,80%,55%)" />
              <KV label="Location" value={stock_item.location} />
              <KV label="Batch" value={stock_item.batch_no} />
            </div>
            <div>
              <KV label="Available" value={`${(stock_item.qty_available || 0).toLocaleString()} ${stock_item.unit}`} />
              <KV label="Reserved" value={`${(stock_item.qty_reserved || 0).toLocaleString()} ${stock_item.unit}`} />
              <KV label="Reorder Level" value={`${(stock_item.reorder_level || 0).toLocaleString()} ${stock_item.unit}`} />
            </div>
          </div>
        </TraceNode>
      );
    }
  }

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
        padding: '10px 16px', background: 'hsl(0,0%,6%)', border: '1px solid hsl(0,0%,14%)' }}>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
          color: 'hsl(0,0%,50%)', textTransform: 'uppercase' }}>Batch:</div>
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
          color: 'hsl(197,100%,48%)', fontWeight: 600 }}>{result.batch_no}</div>
        <span className={result.type === 'FG' ? 'chip-active' : 'chip-info'}>{result.type} BATCH</span>
        <div style={{ marginLeft: 'auto', fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
          color: 'hsl(0,0%,40%)' }}>{nodes.length} nodes in chain</div>
      </div>
      <div style={{ padding: '16px 0' }}>
        {nodes}
      </div>
    </div>
  );
}

export default function Genealogy() {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (batchNo) => {
    const q = batchNo || query;
    if (!q.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await api.get(`/traceability/${encodeURIComponent(q.trim())}`);
      setResult(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const MONO_SM = { fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
    color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em' };

  return (
    <div data-testid="genealogy-page">
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
          color: 'hsl(0,0%,98%)', textTransform: 'uppercase', marginBottom: 4 }}>
          Batch Genealogy
        </h1>
        <div style={MONO_SM}>RM Lot → Production → QC → FG Stock → Dispatch</div>
      </div>

      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: 20, marginBottom: 1 }}>
        <form onSubmit={e => { e.preventDefault(); search(); }}
          style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <MagnifyingGlass size={16} color="hsl(0,0%,40%)" style={{ flexShrink: 0 }} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Enter batch number (e.g. BATCH-FG-2025-042)"
            data-testid="batch-search-input"
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              fontFamily: "'JetBrains Mono', monospace", fontSize: 13,
              color: 'hsl(0,0%,90%)',
            }}
          />
          <button type="submit" className="btn-primary" style={{ flexShrink: 0, padding: '8px 20px' }}
            data-testid="batch-search-btn">
            {loading ? 'Searching...' : 'Trace'}
          </button>
        </form>

        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ ...MONO_SM, marginRight: 4 }}>Try:</span>
          {SAMPLE_BATCHES.map(b => (
            <button key={b} onClick={() => { setQuery(b); search(b); }}
              data-testid={`sample-batch-${b}`}
              style={{
                background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,18%)',
                color: 'hsl(197,100%,48%)', fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, padding: '3px 10px', cursor: 'pointer',
                transition: 'all 0.15s', letterSpacing: '0.04em',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'hsl(197,100%,10%)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'hsl(0,0%,8%)'; }}>
              {b}
            </button>
          ))}
        </div>
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200,
          background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', marginTop: 1 }}>
          <div style={{ color: 'hsl(197,100%,48%)', fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.2em' }}>
            Tracing batch lineage...
          </div>
        </div>
      )}

      {result && !loading && <TraceResult result={result} />}

      {!result && !loading && (
        <div style={{ marginTop: 24, padding: '40px 24px', textAlign: 'center',
          background: 'hsl(0,0%,4%)', border: '1px dashed hsl(0,0%,14%)' }}>
          <ArrowRight size={24} color="hsl(0,0%,25%)" style={{ marginBottom: 12 }} />
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
            color: 'hsl(0,0%,30%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Enter a batch number above to trace its full lineage
          </div>
        </div>
      )}
    </div>
  );
}
